import { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { ClockIcon } from '@heroicons/react/24/outline';

import { cn, EmployerJPState } from '@restworld/utils-common';

import { formatCoveredUntil } from './utils/covered-until';

interface ScheduledOfflineLineProps {
  jpId?: string;
  jpState: EmployerJPState;
  isUsingPPU?: boolean;
  ppuOfflineScheduledAt?: string | null;
  onCancelScheduledOffline?: (jpId: string) => void;
  isCancellingScheduledOffline?: boolean;
  className?: string;
}

/**
 * PPU-only status line for an online JP whose put-offline is scheduled: when it goes offline,
 * with a cancel action. Renders nothing in any other case, so a JP with nothing scheduled looks
 * like every other online JP.
 */
export function ScheduledOfflineLine({
  jpId,
  jpState,
  isUsingPPU,
  ppuOfflineScheduledAt,
  onCancelScheduledOffline,
  isCancellingScheduledOffline,
  className,
}: ScheduledOfflineLineProps) {
  const { t, i18n } = useTranslation('job-positions-page');
  const language = i18n?.language ?? 'it';
  const isVisible = !!isUsingPPU && jpState === 'open';

  const content = useMemo(() => {
    if (!isVisible) return null;
    // Only a scheduled offline shows a line: an online job ad with nothing scheduled looks like
    // every other online job ad (Edoardo, 06/10/2026). The paid-until time is shown in the modal.
    if (!ppuOfflineScheduledAt) return null;
    return formatCoveredUntil(ppuOfflineScheduledAt, t, language);
  }, [isVisible, ppuOfflineScheduledAt, t, language]);

  const handleCancel = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      // The JP card is a link: without this the click would also navigate.
      e.preventDefault();
      e.stopPropagation();
      if (jpId) onCancelScheduledOffline?.(jpId);
    },
    [jpId, onCancelScheduledOffline]
  );

  if (!content) return null;

  const { time, day } = content;

  return (
    <div
      className={cn('flex items-center gap-1 min-w-0 w-full text-xs sm:text-sm text-neutral-600', className)}
      data-testid="scheduled-offline-line"
    >
      <ClockIcon className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span className="truncate min-w-0">{t('scheduled_offline_line', { time, day })}</span>
      {onCancelScheduledOffline && (
        // Native button on purpose: the ui-ds-base Button has no inline text-link variant, and its
        // smallest size (sm, h-8 padded pill) does not fit inside a text-xs status line.
        <button
          type="button"
          onClick={handleCancel}
          disabled={isCancellingScheduledOffline}
          className="shrink-0 ml-1 font-semibold underline text-neutral-700 disabled:text-neutral-200 disabled:cursor-not-allowed"
        >
          {t('scheduled_offline_cancel')}
        </button>
      )}
    </div>
  );
}

export default ScheduledOfflineLine;
